import React, { useState, useEffect, useCallback } from "react";
import "react-clock/dist/Clock.css";
import { FaSpinner } from "react-icons/fa";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import ApiEndPoints from "../../ServicesCalls/ApiEndPoints";
import { getApiTokenFromLocalstorage } from "../reusable-components/PageFormats";

function Admin() {
  const userRoleData = localStorage.getItem("userRole");
  const navigate = useNavigate();
  // Admin table and state
  const [adminTableErr, setAdminTableErr] = useState(null);
  const [adminTableData, setAdminTableData] = useState(null);
  const [isAdminRulesLoading, setIsAdminRulesLoading] = useState(true);
  const [initialAdminTableData, setInitialAdminTableData] = useState([]);
  // Call control
  const [isCallStarted, setIsCallStarted] = useState(false);
  const [isCallStopped, setIsCallStopped] = useState(false);
  const [isRulesModified, setIsRulesModified] = useState(false);
  const [isStartingCalls, setIsStartingCalls] = useState(false);
  // Add user popup and form
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [showAddUserPopup, setShowAddUserPopup] = useState(false);
  const [formData, setFormData] = useState({
    Role: "",
    Email: "",
    Location: "",
    FirstName: "",
    PatientMobile: "",
    Password: "Admin@1234",
  });
  const [formError, setFormError] = useState({ text: "", color: "" });
  // Per-field validation errors for add user form
  const [fieldErrors, setFieldErrors] = useState({
    FirstName: "",
    PatientMobile: "",
    Email: "",
  });

  // Extracted input config for add user popup
  const userInputFields = [
    { key: "FirstName", placeholder: "Name" },
    { key: "PatientMobile", placeholder: "Mobile" },
    { key: "Email", placeholder: "Email" },
    { key: "Location", placeholder: "Location" },
  ];

  const resetFormData = () => {
    setFormData({
      Role: "",
      Email: "",
      Location: "",
      FirstName: "",
      PatientMobile: "",
    });
    setIsSubmittingUser(false);
    setFieldErrors({ FirstName: "", PatientMobile: "", Email: "" });
  };

  // Validate a single field for add user form
  const validateField = (key, value) => {
    switch (key) {
      case "FirstName":
        if (!value.trim()) return "Name is required.";
        if (value.length < 5) return "Name must be at least 5 characters.";
        if (value.length > 30) return "Name cannot exceed 30 characters.";
        return "";
      case "PatientMobile":
        if (!/^\d{10}$/.test(value))
          return "Enter a valid 10‑digit phone number.";
        return "";
      case "Email":
        if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value))
          return "Enter a valid email address.";
        return "";
      default:
        return "";
    }
  };

  // --- Helper functions for admin rules table ---
  const checkRulesReverted = (updatedData) =>
    JSON.stringify(updatedData) === JSON.stringify(initialAdminTableData);

  const updateAdminRuleValue = (ruleId, newValue) => {
    const updated = adminTableData.map((rule) =>
      rule?.Admin_RuleID === ruleId ? { ...rule, RuleValue: newValue } : rule
    );
    setAdminTableData(updated);
    const isReverted = checkRulesReverted(updated);
    setIsRulesModified(!isReverted);
  };

  // Helper to parse time value to 24h string for TimePicker
  function parseTo24Hour(value) {
    const [time, meridian] = value.split(" ");
    let [h, m] = time.split(":");
    h = parseInt(h, 10);
    m = m ? m.padStart(2, "0") : "00";
    let h24 = h;
    if (meridian === "PM" && h < 12) h24 = h + 12;
    if (meridian === "AM" && h === 12) h24 = 0;
    return `${String(h24).padStart(2, "0")}:${m}`;
  }

  // Helper to render the correct rule input type
  function renderRuleInputField(rule) {
    const value = rule?.RuleValue;
    // Boolean radio
    if (["Yes", "No"].includes(value)) {
      return (
        <div className="flex gap-4">
          {["Yes", "No"].map((option) => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="radio"
                checked={value === option}
                onChange={() => updateAdminRuleValue(rule.Admin_RuleID, option)}
                className="cursor-pointer"
              />
              {option}
            </label>
          ))}
        </div>
      );
    }
    // Start/Stop radio (disabled)
    if (["Start", "Stop"].includes(value)) {
      return (
        <div className="flex gap-4">
          {["Start", "Stop"].map((option) => (
            <label key={option} className="flex items-center gap-1">
              <input type="radio" checked={value === option} disabled={true} />
              {option}
            </label>
          ))}
        </div>
      );
    }
    // Numeric field with stepper
    if (/^\d+$/.test(value) || value === "") {
      const numericValue = Number(value);
      return (
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 bg-blue-300 rounded disabled:opacity-50"
            disabled={numericValue <= 1}
            onClick={() =>
              updateAdminRuleValue(
                rule.Admin_RuleID,
                String(Math.max(1, numericValue - 1))
              )
            }
          >
            -
          </button>
          <input
            type="number"
            value={value}
            min="1"
            max="30"
            onChange={(e) => {
              const newVal = e.target.value;
              if (newVal === "") {
                updateAdminRuleValue(rule.Admin_RuleID, "");
              } else if (/^\d*$/.test(newVal)) {
                const next = Number(newVal);
                if (next >= 1 && next <= 30) {
                  updateAdminRuleValue(rule.Admin_RuleID, newVal);
                }
              }
            }}
            onBlur={() => {
              if (value === "" || isNaN(value) || Number(value) < 1) {
                updateAdminRuleValue(rule.Admin_RuleID, "1");
              }
            }}
            className="w-16 px-2 py-1 border border-black rounded"
          />
          <button
            className="px-2 py-1 bg-blue-300 rounded disabled:opacity-50"
            disabled={numericValue >= 30}
            onClick={() =>
              updateAdminRuleValue(
                rule.Admin_RuleID,
                String(Math.min(30, numericValue + 1))
              )
            }
          >
            +
          </button>
        </div>
      );
    }
    // Time (12h)
    if (/^\d{1,2}:\d{2} ?(AM|PM)$/i.test(value)) {
      return (
        <>
          <TimePicker
            value={parseTo24Hour(value)}
            disableClock
            clearIcon={null}
            clockIcon={null}
            format="hh:mm a"
            onChange={(val) => {
              const [newH, newM] = val.split(":");
              let hourNum = parseInt(newH, 10);
              const meridianOut = hourNum >= 12 ? "PM" : "AM";
              const displayHour = hourNum % 12 === 0 ? 12 : hourNum % 12;
              const finalTime = `${String(displayHour).padStart(
                2,
                "0"
              )}:${newM} ${meridianOut}`;
              updateAdminRuleValue(rule.Admin_RuleID, finalTime);
            }}
            className="w-[120px] px-2 py-1 text-sm border border-gray-400 rounded"
            clockClassName="hidden"
          />
          <span className="ml-2">PST</span>
        </>
      );
    }
    // Default text field
    return (
      <input
        type="text"
        value={value}
        onChange={(e) =>
          updateAdminRuleValue(rule.Admin_RuleID, e.target.value)
        }
        className="w-1/2 px-2 py-1 border border-black rounded"
      />
    );
  }

  const fetchAdminRulesData = useCallback(async () => {
    try {
      setAdminTableErr(null);
      const requestHeaders = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
        },
      };
      const response = await fetch(
        ApiEndPoints.getAdminRules(),
        requestHeaders
      );
      const data = await response.json();
      const dataMsg = data?.message;

      if (!response.ok)
        throw new Error(dataMsg || "Failed to Fetch AdminRules API");

      const dataAdmin = data?.Adminrules;
      const arrDataRes =
        data && dataAdmin
          ? Array.isArray(dataAdmin)
            ? dataAdmin
            : [dataAdmin]
          : null;
      let parsedData = [...arrDataRes].sort((a, b) =>
        a?.Admin_RuleID.localeCompare(b?.Admin_RuleID, undefined, {
          numeric: true,
        })
      );
      // Normalize RuleValue for "SMS follow up for confirmed appointments"
      parsedData = parsedData.map((rule) => {
        if (rule?.Rule === "SMS follow up for confirmed appointments") {
          const val = String(rule?.RuleValue).toLowerCase();
          if (["true", "True", "Yes", "yes"].includes(val))
            rule.RuleValue = "Yes";
          else if (["false", "False", "No", "no"].includes(val))
            rule.RuleValue = "No";
        }
        return rule;
      });
      setAdminTableData(parsedData);
      setInitialAdminTableData(parsedData);
    } catch (err) {
      const errorMsg = err.message;
      console.error(errorMsg);
      setAdminTableErr(errorMsg);
      setAdminTableData(null);
      return;
    } finally {
      setIsAdminRulesLoading(false);
    }
  }, []);

  const fetchUpdateAdminRules = useCallback(async () => {
    setIsAdminRulesLoading(true);
    try {
      setAdminTableErr(null);
      // Only send the three expected backend keys with exact UI values
      const requestBody = {};
      adminTableData.forEach(({ Rule, RuleValue }) => {
        switch (Rule) {
          case "Schedule time":
            requestBody["ScheduleTime"] = String(RuleValue);
            break;
          case "Max number of patients being called in one batch":
            requestBody["NumberOfStreams"] = String(RuleValue);
            break;
          case "SMS follow up for confirmed appointments":
            if (RuleValue === "Yes") requestBody["SMSFollow-Up"] = "true";
            else if (RuleValue === "No") requestBody["SMSFollow-Up"] = "false";
            break;
          default:
            break;
        }
      });
      const requestHeaders = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
        },
        body: JSON.stringify(requestBody),
      };
      const response = await fetch(
        ApiEndPoints.postAdminRulesUpdate(),
        requestHeaders
      );
      const data = await response.json();
      const dataSuccess = data?.message;
      const dataError = data?.error;

      if (!response.ok)
        throw new Error(
          dataSuccess || dataError || "Failed to Fetch Update AdminRules API"
        );

      toast.success(dataSuccess);
      setTimeout(() => {
        fetchAdminRulesData();
      }, [1500]);
    } catch (err) {
      const errorMsg = err.message;
      toast.error(errorMsg);
      setAdminTableErr(errorMsg);
      return;
    } finally {
      setIsAdminRulesLoading(false);
    }
  }, [adminTableData, fetchAdminRulesData]);

  // --- Add User API Handler ---
  const fetchAddUser = async (e) => {
    try {
      const finalFormData = {
        ...formData,
        Username: formData.Email,
      };
      const requestHeaders = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
        },
        body: JSON.stringify(finalFormData),
      };
      const response = await fetch(
        ApiEndPoints.postAdminAddUsers(),
        requestHeaders
      );
      const data = await response.json();
      const dataSuccess = data?.message;
      const dataError = data?.error;

      if (!response.ok)
        throw new Error(
          dataSuccess || dataError || "Failed to Fetch AddUsers API"
        );

      setFormError({ text: dataSuccess, color: "green" });
      resetFormData();
      // Send default password mail after user is added
      const apiResponse = await fetch(
        ApiEndPoints.poastLoginForgotPasswordMail(),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
          },
          body: JSON.stringify({ email: formData.Email }),
        }
      );
      const result = await apiResponse.json();
      const resultSuccess = result?.message;
      const resultError = result?.error;
      if (!apiResponse.ok)
        throw new Error(
          resultSuccess || resultError || "Failed to Fetch ForgotPassword API"
        );

      setFormError({ text: resultSuccess, color: "green" });
    } catch (err) {
      const errorMsg = err.message;
      console.error(errorMsg);
      setFormError({ text: errorMsg, color: "red" });
      setIsSubmittingUser(false);
    } finally {
      setTimeout(() => {
        setFormError({ text: "", color: "" });
      }, 5000);
    }
  };

  useEffect(() => {
    fetchAdminRulesData();
  }, [fetchAdminRulesData]);

  // Helper for call initiation
  const fetchCallInitiation = async (ruleValue) => {
    try {
      if (ruleValue === "Start") setIsCallStarted(true);
      else if (ruleValue === "Stop") setIsCallStopped(true);
      setIsStartingCalls(true);
      const requestHeaders = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
        },
        body: JSON.stringify({ RuleValue: ruleValue }),
      };
      const response = await fetch(
        ApiEndPoints.postAdminPatientCalls(),
        requestHeaders
      );
      const data = await response.json();
      const dataSuccess = data?.message;
      const dataError = data?.error;

      if (!response.ok)
        throw new Error(
          dataSuccess || dataError || "Failed to Fetch CallInitiate API"
        );

      toast.success(dataSuccess);
      setTimeout(() => {
        fetchAdminRulesData();
      }, 2500);
      if (ruleValue === "Start") {
        setTimeout(() => setIsCallStarted(false), 1500);
      } else if (ruleValue === "Stop") {
        setTimeout(() => setIsCallStopped(false), 1500);
      }
    } catch (err) {
      const errorMsg = err.message;
      console.error(errorMsg);
      toast.error(errorMsg);
      if (ruleValue === "Start") setIsCallStarted(false);
      else if (ruleValue === "Stop") setIsCallStopped(false);
    } finally {
      setIsStartingCalls(false);
    }
  };

  const handleAddUsers = () => {
    setFormError({ text: "", color: "" });
    setShowAddUserPopup(true);
  };

  const handleAddUserForm = (e) => {
    e.preventDefault();
    setIsSubmittingUser(true);
    // Validate all fields before submitting
    const newErrors = {
      FirstName: validateField("FirstName", formData.FirstName),
      PatientMobile: validateField("PatientMobile", formData.PatientMobile),
      Email: validateField("Email", formData.Email),
    };
    setFieldErrors(newErrors);
    // If any error messages exist, abort submit
    if (Object.values(newErrors).some((msg) => msg)) return;
    setFormError({ text: "", color: "" });
    fetchAddUser();
  };

  const handleCloseUserForm = () => {
    setShowAddUserPopup(false);
    setFormError({ text: "", color: "" });
    resetFormData();
  };

  return (
    <>
      <ToastContainer />
      <div className="px-4 py-6 mx-3">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_3fr]">
          <div className="p-4 bg-white rounded-lg shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Admin Options</h2>
            </div>
            <div className="flex flex-col items-center justify-center gap-6 mt-3 text-center">
              {userRoleData === "System Admin" && (
                <button
                  onClick={handleAddUsers}
                  className="min-w-[215px] px-4 py-2 bg-green-600 hover:bg-green-600 border border-black text-white rounded-md text-md font-bold"
                >
                  Add Users
                </button>
              )}
              <button
                className={`min-w-[215px] px-4 py-2 ${
                  isCallStarted
                    ? "bg-gray-400"
                    : "bg-purple-600 hover:bg-purple-600"
                } border border-black text-white rounded-md text-md font-bold`}
                disabled={isCallStarted || isStartingCalls}
                onClick={() => fetchCallInitiation("Start")}
              >
                {isCallStarted
                  ? "Started Patient Calls"
                  : "Configure Patient Calls"}
              </button>
              <button
                className={`min-w-[215px] px-4 py-2 ${
                  isCallStopped ? "bg-gray-400" : "bg-red-500 hover:bg-red-500"
                } border border-black text-white rounded-md text-md font-bold`}
                disabled={isCallStopped || isStartingCalls}
                onClick={() => fetchCallInitiation("Stop")}
              >
                {isCallStopped
                  ? "Stopped Patient Calls"
                  : "Stop AI Agent Calls"}
              </button>
              <div className="w-full mt-6">
                <h2 className="mb-3 text-2xl font-bold text-left">Reports</h2>
                <button
                  onClick={() => navigate("/admin/calls")}
                  className="min-w-[215px] px-4 py-2 bg-sky-400 hover:bg-sky-400 border border-black text-white rounded-md text-md font-bold"
                >
                  Open & Completed Calls
                </button>
              </div>
            </div>
          </div>
          {isAdminRulesLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 pr-2 mr-3 border-t-2 border-blue-800 rounded-full animate-spin"></div>
              <span className="py-8 text-center text-blue-500">
                Loading Admin Rules Data...
              </span>
            </div>
          ) : adminTableErr ? (
            <div className="py-8 text-center text-red-600">{adminTableErr}</div>
          ) : (
            <div className="flex flex-col gap-6 px-4 sm:px-6 lg:px-8">
              <div className="p-4 bg-white rounded-lg shadow-md sm:p-6">
                {/* Header with title and Apply Changes button */}
                <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="text-xl font-bold sm:text-2xl">
                    Configurable Rules
                  </h2>
                  <button
                    className={`px-4 py-2 text-sm font-semibold text-white rounded ${
                      isRulesModified
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    disabled={!isRulesModified}
                    onClick={() => {
                      fetchUpdateAdminRules();
                      setIsRulesModified(false);
                    }}
                  >
                    Apply Changes
                  </button>
                </div>

                {/* Responsive Rules Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <tbody>
                      {adminTableData
                        ?.filter(
                          (rule) =>
                            !(
                              rule?.RuleValue === "Start" ||
                              rule?.RuleValue === "Stop"
                            )
                        )
                        .map((adminRule) => (
                          <tr
                            key={adminRule?.Admin_RuleID}
                            className="border-b last:border-0"
                          >
                            <td className="py-4">
                              {/* Responsive Row Layout */}
                              <div className="flex flex-col w-full gap-2 md:flex-row md:items-center md:gap-4">
                                <label className="font-medium text-gray-700 xl:w-1/2 md:w-2/3 lg:w-full sm:w-1/2">
                                  {adminRule?.Rule}
                                </label>
                                <div className="xl:w-2/3 lg:w-1/2 md:w-2/3 sm:w-full">
                                  {renderRuleInputField(adminRule)}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Add User Popup Modal */}
        {showAddUserPopup && (
          <form
            onSubmit={handleAddUserForm}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
          >
            <div className="w-full max-w-md p-6 bg-white rounded shadow-lg">
              <h3 className="mb-4 text-xl font-semibold text-center">
                Add New User
              </h3>
              {formError.text && (
                <p
                  className={`mt-4 mb-2 text-sm text-center ${
                    formError.color === "green"
                      ? "text-green-600"
                      : formError.color === "red"
                      ? "text-red-600"
                      : ""
                  }`}
                >
                  {formError.text}
                </p>
              )}
              {isSubmittingUser && (
                <div className="flex items-center justify-center gap-2 mt-4 mb-2 text-gray-700">
                  <FaSpinner
                    size={20}
                    className="w-6 h-6 text-blue-500 animate-spin"
                  />
                  <span>Please wait...</span>
                </div>
              )}
              {userInputFields.map(({ key, placeholder }) => {
                if (key === "FirstName") {
                  return (
                    <React.Fragment key={key}>
                      <input
                        type="text"
                        placeholder={placeholder}
                        value={formData[key]}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        onBlur={(e) =>
                          setFieldErrors((prev) => ({
                            ...prev,
                            [key]: validateField(key, e.target.value),
                          }))
                        }
                        className="w-full px-3 py-2 mb-1 border border-gray-300 rounded"
                        required
                        minLength={5}
                        maxLength={30}
                        pattern=".{5,30}"
                      />
                      {fieldErrors[key] && (
                        <p className="mb-1 text-sm text-red-600">
                          {fieldErrors[key]}
                        </p>
                      )}
                    </React.Fragment>
                  );
                }
                if (key === "PatientMobile") {
                  return (
                    <React.Fragment key={key}>
                      <input
                        type="tel"
                        placeholder={placeholder}
                        value={formData[key]}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            setFormData((prev) => ({
                              ...prev,
                              [key]: value,
                            }));
                          }
                        }}
                        onBlur={(e) =>
                          setFieldErrors((prev) => ({
                            ...prev,
                            [key]: validateField(key, e.target.value),
                          }))
                        }
                        className="w-full px-3 py-2 mb-1 border border-gray-300 rounded"
                        required
                        pattern="\d{10}"
                        title="Enter a valid 10-digit phone number"
                      />
                      {fieldErrors[key] && (
                        <p className="mb-1 text-sm text-red-600">
                          {fieldErrors[key]}
                        </p>
                      )}
                    </React.Fragment>
                  );
                }
                if (key === "Email") {
                  return (
                    <React.Fragment key={key}>
                      <input
                        type="email"
                        placeholder={placeholder}
                        value={formData[key]}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        onBlur={(e) =>
                          setFieldErrors((prev) => ({
                            ...prev,
                            [key]: validateField(key, e.target.value),
                          }))
                        }
                        className="w-full px-3 py-2 mb-1 border border-gray-300 rounded"
                        required
                        pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                        title="Enter a valid email address"
                      />
                      {fieldErrors[key] && (
                        <p className="mb-1 text-sm text-red-600">
                          {fieldErrors[key]}
                        </p>
                      )}
                    </React.Fragment>
                  );
                }
                // Default input for other fields
                return (
                  <input
                    key={key}
                    type="text"
                    placeholder={placeholder}
                    value={formData[key]}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 mb-3 border border-gray-300 rounded"
                    required
                  />
                );
              })}
              <select
                value={formData.Role}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, Role: e.target.value }))
                }
                className="w-full px-3 py-2 mb-3 border border-gray-300 rounded"
              >
                <option value="">Select Role</option>
                <option value="Clinical Admin">Clinical Admin</option>
                <option value="System Admin">System Admin</option>
              </select>
              <div className="flex justify-between">
                <button
                  type="submit"
                  disabled={isSubmittingUser}
                  className={`px-4 py-2 text-white rounded ${
                    isSubmittingUser
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  Add User
                </button>
                <button
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                  onClick={handleCloseUserForm}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

export default Admin;
